import React, { Fragment, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Form from './Form';
import FrontCanvas from './FrontCanvas';
import './ApparelEditor.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as selectors from './ApparelEditor.selectors';
import * as actions from './ApparelEditor.actions';
import { stringifyEqualityCheck } from './../../utils';
import * as loginSelectors from './../Login/Login.selectors';
import { Link } from 'react-router-dom';
import * as dialog from './../common/dialog';

const ApparelEditor = (props) => {
    /* State to props */
    const templateCode = useSelector(selectors.getTemplateCode);
    const quantity = useSelector(selectors.getQuantity);
    const size = useSelector(selectors.getSize);
    const logo = useSelector(selectors.getLogo);
    const apparelItem = useSelector(selectors.getApparelItem, stringifyEqualityCheck);
    const fetching = useSelector(selectors.getFetching);
    const addingToCart = useSelector(selectors.getAddingToCart);
    const persons = useSelector(selectors.getPersons);
    const department = useSelector(selectors.getDepartment, stringifyEqualityCheck);
    const fetchingDepartment = useSelector(selectors.getFetchingDepartment);
    const checkingShirtsIndex = useSelector(selectors.getCheckingShirtsIndex);
    const checkingShirts = useSelector(selectors.getCheckingShirts);
    const managerClinicName = useSelector(selectors.getManagerClinicName);
    const managerName = useSelector(selectors.getManagerName);
    const managerEmail = useSelector(selectors.getManagerEmail);
    const managerAddress = useSelector(selectors.getManagerAddress);
    const managerCity = useSelector(selectors.getManagerCity);
    const managerState = useSelector(selectors.getManagerState);
    const managerZip = useSelector(selectors.getManagerZip);
    const managerPhone = useSelector(selectors.getManagerPhone);
    const personalizationEnabled = useSelector(selectors.getPersonalizationEnabled);
    const isFavorite = useSelector(selectors.getIsFavorite);
    const checkingFavorite = useSelector(selectors.getCheckingFavorite);
    const savingWork = useSelector(selectors.getSavingWork);
    const action = useSelector(selectors.getAction);
    const variables = useSelector(selectors.getVariables, stringifyEqualityCheck);
    const cxmlData = useSelector(loginSelectors.getCxmlData, stringifyEqualityCheck);
    const platformType = useSelector(loginSelectors.getPlatformType);
    const user = useSelector(loginSelectors.getUser, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const fetchApparelItem = useCallback((id, departmentId) => dispatch(actions.fetchApparelItem(id, departmentId)), [dispatch]);
    const setTemplateCode = useCallback((value) => dispatch(actions.setTemplateCode(value)), [dispatch]);
    const setQuantity = useCallback((value) => dispatch(actions.setQuantity(value)), [dispatch]);
    const setSize = useCallback((value) => dispatch(actions.setSize(value)), [dispatch]);
    const setLogo = useCallback((value) => dispatch(actions.setLogo(value)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const addToCart = useCallback((orderType, history, type) => dispatch(actions.addToCart(orderType, history, type)), [dispatch]);
    const addPerson = useCallback((sizes, logosJson) => dispatch(actions.addPerson(sizes, logosJson)), [dispatch]);
    const removePerson = useCallback((index) => dispatch(actions.removePerson(index)), [dispatch]);
    const setPersonField = useCallback((personIndex, fieldIndex, value) => dispatch(actions.setPersonField(personIndex, fieldIndex, value)), [dispatch]);
    const setPersonQuantity = useCallback((personIndex, quantityIndex, value) => dispatch(actions.setPersonQuantity(personIndex, quantityIndex, value)), [dispatch]);
    const initPersons = useCallback((sizes, logosJson, department) => dispatch(actions.initPersons(sizes, logosJson, department)), [dispatch]);
    const fetchDepartment = useCallback((id) => dispatch(actions.fetchDepartment(id)), [dispatch]);
    const checkShirts = useCallback((uthId, index) => dispatch(actions.checkShirts(uthId, index)), [dispatch]);
    const setManagerClinicName = useCallback((value) => dispatch(actions.setManagerClinicName(value)), [dispatch]);
    const setManagerName = useCallback((value) => dispatch(actions.setManagerName(value)), [dispatch]);
    const setManagerEmail = useCallback((value) => dispatch(actions.setManagerEmail(value)), [dispatch]);
    const setManagerAddress = useCallback((value) => dispatch(actions.setManagerAddress(value)), [dispatch]);
    const setManagerCity = useCallback((value) => dispatch(actions.setManagerCity(value)), [dispatch]);
    const setManagerState = useCallback((value) => dispatch(actions.setManagerState(value)), [dispatch]);
    const setManagerZip = useCallback((value) => dispatch(actions.setManagerZip(value)), [dispatch]);
    const setManagerPhone = useCallback((value) => dispatch(actions.setManagerPhone(value)), [dispatch]);
    const setPersonalizationEnabled = useCallback((value) => dispatch(actions.setPersonalizationEnabled(value)), [dispatch]);
    const addFavorite = useCallback((history) => dispatch(actions.addFavorite(history)), [dispatch]);
    const checkFavorite = useCallback((templateCode) => dispatch(actions.checkFavorite(templateCode)), [dispatch]);
    const setAction = useCallback((value) => dispatch(actions.setAction(value)), [dispatch]);
    const loadVariables = useCallback(() => dispatch(actions.loadVariables()), [dispatch]);
    const setField = useCallback((field, value) => dispatch(actions.setField(field, value)), [dispatch]);
    const addFile = useCallback((file) => dispatch(actions.addFile(file)), [dispatch]);
    const removeFile = useCallback((index) => dispatch(actions.removeFile(index)), [dispatch]);
    const clearFiles = useCallback(() => dispatch(actions.clearFiles()), [dispatch]);
    const showHelpModal = useCallback(() => dispatch(dialog.actions.showDialog('help-modal')), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        const ignore = props.match.params.ignore;
        if (!ignore || parseInt(ignore) !== 1) {
            resetState();
            fetchApparelItem(props.match.params.id, props.match.params.departmentId);
            fetchDepartment(props.match.params.departmentId);
        }

        if (props.match.params.action === 'edit') {
            loadVariables();
            setAction('edit');
        } else if (props.match.params.action === 'reorder') {
            loadVariables();
            setAction('reorder');
        }

        setTemplateCode(props.match.params.id);
        checkFavorite(props.match.params.id);

        return () => {
            resetState();
        };
    }, [setTemplateCode, resetState, fetchApparelItem, fetchDepartment, checkFavorite, loadVariables, setAction,
        props.match.params.id, props.match.params.departmentId, props.match.params.ignore, props.match.params.action]);

    useEffect(() => {
        if (apparelItem == null || department == null) {
            return;
        }

        const ignore = props.match.params.ignore;
        if (!ignore || parseInt(ignore) !== 1) {
            const sizesJson = JSON.parse(apparelItem.sizes_options_json);
            const logosJson = JSON.parse(apparelItem.logos_options_json);
            initPersons(sizesJson, logosJson, department);
        }
    }, [initPersons, apparelItem, department, setLogo, props.match.params.ignore]);

    let content = <div className="text-center"><i className="fas fa-circle-notch fa-spin" /> Loading data...</div>;
    if (!fetching) {
        content = (
            <div className="steps-content">
                <div className="left-side-container">
                    <Form
                        quantity={quantity}
                        size={size}
                        logo={logo}
                        cxmlData={cxmlData}
                        templateCode={templateCode}
                        apparelItem={apparelItem}
                        fetching={fetching}
                        addingToCart={addingToCart}
                        persons={persons}
                        department={department}
                        fetchingDepartment={fetchingDepartment}
                        checkingShirtsIndex={checkingShirtsIndex}
                        checkingShirts={checkingShirts}
                        managerClinicName={managerClinicName}
                        managerName={managerName}
                        managerEmail={managerEmail}
                        managerAddress={managerAddress}
                        managerCity={managerCity}
                        managerState={managerState}
                        managerZip={managerZip}
                        managerPhone={managerPhone}
                        personalizationEnabled={personalizationEnabled}
                        platformType={platformType}
                        variables={variables}
                        user={user}
                        history={props.history}
                        productId={props.match.params.productId}
                        setQuantity={setQuantity}
                        setSize={setSize}
                        setLogo={setLogo}
                        addToCart={addToCart}
                        addPerson={addPerson}
                        removePerson={removePerson}
                        setPersonField={setPersonField}
                        setPersonQuantity={setPersonQuantity}
                        checkShirts={checkShirts}
                        setManagerClinicName={setManagerClinicName}
                        setManagerName={setManagerName}
                        setManagerEmail={setManagerEmail}
                        setManagerAddress={setManagerAddress}
                        setManagerCity={setManagerCity}
                        setManagerState={setManagerState}
                        setManagerZip={setManagerZip}
                        setManagerPhone={setManagerPhone}
                        setPersonalizationEnabled={setPersonalizationEnabled}
                        isFavorite={isFavorite}
                        addFavorite={addFavorite}
                        checkingFavorite={checkingFavorite}
                        savingWork={savingWork}
                        action={action}
                        showHelpModal={showHelpModal}
                        setField={setField}
                        addFile={addFile}
                        removeFile={removeFile}
                        clearFiles={clearFiles}
                    />
                </div>
                <div className="right-side-container">
                    <FrontCanvas
                        templateCode={templateCode}
                        templateId={props.match.params.id}
                        apparelItem={apparelItem}
                        fetching={fetching}
                        variables={variables}
                    />
                </div>
            </div>
        );
    }

    let breadcrumbsContainer = null;
    if (parseInt(props.match.params.productId) === 7 && apparelItem != null) {
        const breadcrumbs = [<Link to="/utp">Main Categories</Link>, ' / ', <Link to="/product/7">Apparel Categories</Link>];

        for (let i = 0; i < apparelItem.breadcrumbs.length; i++) {
            if (i === apparelItem.breadcrumbs.length - 1) {
                breadcrumbs.push(' / ', <Link to={'/department/7/' + apparelItem.breadcrumbs[i].id}>{apparelItem.breadcrumbs[i].label}</Link>);
            } else {
                breadcrumbs.push(' / ', <Link to={'/product/7/' + apparelItem.breadcrumbs[i].id}>{apparelItem.breadcrumbs[i].label}</Link>);
            }
        }

        let color = '';
        if (parseInt(apparelItem.logic_version) === 2) {
            color = ' (' + apparelItem.color + ')';
        }

        breadcrumbs.push(' / ', apparelItem.name + color);
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    }

    // let cxmlEmail = '';
    // if (cxmlData && cxmlData.email) {
    //     cxmlEmail = cxmlData.email.toLowerCase();
    // }

    return (
        <Layout
            title={<Fragment><i className="fas fa-paint-brush"></i> Editor</Fragment>}
            step={3}
            history={props.history}
        >
            <Helmet><title>Editor - UT Physicians</title></Helmet>
            <div className="apparel-editor-container">
                {breadcrumbsContainer}
                {content}
            </div>
        </Layout>
    );
};

export default ApparelEditor;
